import { effect, inject, Injectable, signal } from '@angular/core';
import { categoriesEN, categoriesIT } from './static/categories';
import { LocaleService } from './locale';

interface PersistableCategoriesPayload {
  categories: any[];
  serialized: string;
}

@Injectable({
  providedIn: 'root',
})
export class CategoryService {
  categories = signal(categoriesIT);
  blockNavigation = signal(false);
  private persistTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly persistDelayMs = 250;
  private readonly maxPersistedLength = 400_000;

  private localeService = inject(LocaleService);

  constructor() {
    effect(() => {
      this.setCategories();
    });
  }

  persistCategoriesImmediately(categories: any[]): boolean {
    return this.writeCategories(categories, true);
  }

  persistCategories(categories: any[]): void {
    this.categories.set(categories);

    if (this.persistTimer) {
      clearTimeout(this.persistTimer);
    }

    this.persistTimer = setTimeout(() => {
      this.writeCategories(categories, false);
    }, this.persistDelayMs);
  }

  /**
   * Schedules a deferred localStorage write using a pre-serialized payload.
   * Avoids blocking the main thread with synchronous large writes.
   */
  deferLocalStorageWrite(serialized: string): void {
    if (this.persistTimer) {
      clearTimeout(this.persistTimer);
    }
    this.persistTimer = setTimeout(() => {
      if (serialized.length <= this.maxPersistedLength) {
        localStorage.setItem('categories', serialized);
      }
    }, this.persistDelayMs);
  }

  private writeCategories(categories: any[], updateSignal: boolean): boolean {
    const payload = this.prepareCategoriesForPersistence(categories);

    if (!payload) {
      localStorage.removeItem('categories');
      if (updateSignal) {
        this.categories.set(this.getStaticCategories());
      }
      return false;
    }

    localStorage.setItem('categories', payload.serialized);
    if (updateSignal) {
      this.categories.set(payload.categories);
    }
    return true;
  }

  prepareCategoriesForPersistence(
    categories: any[],
  ): PersistableCategoriesPayload | null {
    try {
      const persistableCategories = this.buildPersistableCategories(categories);
      const serialized = JSON.stringify(persistableCategories);

      // Keep localStorage writes bounded to avoid UI freezes in Chromium.
      if (serialized.length > this.maxPersistedLength) {
        console.warn(
          'Skipping oversized categories payload while persisting to localStorage.',
        );
        return null;
      }

      return { categories: persistableCategories, serialized };
    } catch (error) {
      console.error('Failed to prepare categories for persistence:', error);
      return null;
    }
  }

  private buildPersistableCategories(categories: any[]): any[] {
    return categories.map((category: any) => ({
      ...category,
      questions: Array.isArray(category?.questions)
        ? category.questions.map((question: any) => ({
            ...question,
            emissions: this.sanitizeEmissions(question?.emissions),
          }))
        : category?.questions,
      uncertaintyMatrix: Array.isArray(category?.uncertaintyMatrix)
        ? category.uncertaintyMatrix.map((row: any) => ({
            ...row,
            climatiqResult: this.sanitizeClimatiqResult(row?.climatiqResult),
            documents: Array.isArray(row?.documents)
              ? row.documents.map(({ base64, ...rest }: any) => rest)
              : row?.documents,
          }))
        : category?.uncertaintyMatrix,
    }));
  }

  private sanitizeEmissions(emissions: any): any {
    if (!Array.isArray(emissions)) {
      return emissions;
    }

    return emissions.map((emission: any) => this.sanitizeEmission(emission));
  }

  private sanitizeEmission(emission: any): any {
    if (!emission || typeof emission !== 'object') {
      return emission;
    }

    if (emission.estimate) {
      return {
        ...emission,
        estimate: this.sanitizeClimatiqResult(emission.estimate),
      };
    }

    return this.sanitizeClimatiqResult(emission);
  }

  private sanitizeClimatiqResult(result: any): any {
    if (!result || typeof result !== 'object') {
      return result;
    }

    return {
      co2e: result.co2e,
      co2e_unit: result.co2e_unit,
      constituent_gases: result.constituent_gases,
      co2e_calculation_method: result.co2e_calculation_method,
      co2e_calculation_origin: result.co2e_calculation_origin,
      activity_data: result.activity_data,
      emission_factor: this.sanitizeEmissionFactor(result.emission_factor),
    };
  }

  private sanitizeEmissionFactor(emissionFactor: any): any {
    if (!emissionFactor || typeof emissionFactor !== 'object') {
      return emissionFactor;
    }

    return {
      activity_id: emissionFactor.activity_id,
      id: emissionFactor.id,
      name: emissionFactor.name,
      region: emissionFactor.region,
      source: emissionFactor.source,
      source_dataset: emissionFactor.source_dataset,
      year: emissionFactor.year,
      unit: emissionFactor.unit,
    };
  }

  setCategories() {
    const rawCategories = localStorage.getItem('categories');

    if (rawCategories) {
      // Defensive cutoff: huge JSON payloads (typically from embedded base64) can freeze the UI.
      if (rawCategories.length > this.maxPersistedLength) {
        console.warn(
          'Skipping oversized categories payload from localStorage to avoid browser lockup.',
        );
        localStorage.removeItem('categories');
        this.categories.set(this.getStaticCategories());
        return;
      }

      let categories: any;
      try {
        categories = JSON.parse(rawCategories);
      } catch (error) {
        console.error('Failed to parse categories from localStorage:', error);
        localStorage.removeItem('categories');
        this.categories.set(this.getStaticCategories());
        return;
      }

      if (categories) {
        categories = this.buildPersistableCategories(categories);
        let init = this.getStaticCategories();

        categories.map((category: any, cIndex: any) => {
          category.questions.map((question: any, qIndex: any) => {
            const staticQ = init[cIndex]?.questions[qIndex];
            question.title = staticQ?.title;
            question.heading = staticQ?.heading;
            question.doubt = staticQ?.doubt;
            // Restore hidden flag from static definition; clear stale emissions on hidden questions
            question.hidden = staticQ?.hidden ?? false;
            if (question.hidden) {
              question.emissions = [];
            }
            // One-time migration: Cat 3 Q2/Q3/Q4 had annualization added —
            // clear cached (non-annualized) emissions exactly once.
            const MIGRATION_KEY = 'cat3_annualization_migrated_v1';
            if (!localStorage.getItem(MIGRATION_KEY)) {
              if (
                cIndex === 2 &&
                (qIndex === 2 || qIndex === 3 || qIndex === 4)
              ) {
                question.emissions = [];
              }
            }
          });
        });

        // Mark the one-time Cat3 annualization migration as done
        localStorage.setItem('cat3_annualization_migrated_v1', '1');

        this.categories.set(categories);
      }
    } else {
      this.categories.set(this.getStaticCategories());
    }
  }

  private getStaticCategories() {
    return this.localeService.getLanguage() === 'english'
      ? categoriesEN
      : categoriesIT;
  }
}
